import { useEffect, useRef, useState } from 'react';

// Persists which video was showing across mount/unmount cycles — AppShell
// conditionally mounts/unmounts this component every time the kiosk goes
// idle then active again (see src/App.js), so plain useState would always
// restart at video 0. Module-level (not React state/context) because this
// component only ever has one instance on screen at a time. Naturally
// resets to 0 once the queue wraps past the last video (see advance()
// below) — a full page reload also resets it, which is fine.
let lastPlayedIndex = 0;

// Test-only: clears the module-level position so test files don't leak
// state into each other.
export const __resetKioskLoopPositionForTests = () => { lastPlayedIndex = 0; };

// A picker left open with nobody around closes itself and the loop carries on.
export const PICKER_IDLE_CLOSE_MS = 30 * 1000;

// Taps inside the "Videos" button and the picker must not count as waking the
// kiosk (useKioskIdle ignores anything inside [data-kiosk-keep-idle]), and must
// not reach the overlay's tap-to-dismiss handlers either.
const stopTap = (e) => e.stopPropagation();

// Fullscreen idle-mode video loop for the kiosk, ported from the BluppiTV
// project. Cycles through `playlist` (rows from /api/kiosk-loop-videos, each
// with a video_url — see AdminKioskLoopPage.js for how they're managed); any
// tap dismisses it via onDismiss (wired in src/App.js to send the visitor to
// the artist catalog), except on the "Videos" button, which opens a preview
// grid so the visitor can pick which video plays.
const KioskAttractLoop = ({ playlist, onDismiss }) => {
  const [index, setIndex] = useState(() => (playlist.length ? lastPlayedIndex % playlist.length : 0));
  const [progress, setProgress] = useState(0);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerActivity, setPickerActivity] = useState(0);
  const videoRef = useRef(null);
  const current = playlist[index % playlist.length];

  // Restarts on every tap inside the picker (pickerActivity).
  useEffect(() => {
    if (!pickerOpen) return undefined;
    const timer = setTimeout(() => setPickerOpen(false), PICKER_IDLE_CLOSE_MS);
    return () => clearTimeout(timer);
  }, [pickerOpen, pickerActivity]);

  const openPicker = (e) => {
    e.stopPropagation();
    setPickerOpen(true);
  };

  const choose = (i) => {
    setIndex(i);
    setPickerOpen(false);
  };

  useEffect(() => {
    lastPlayedIndex = index;
    setProgress(0);
    const node = videoRef.current;
    if (!node) return;
    node.play().catch(() => {});
  }, [index]);

  // Wraps back to 0 after the last video — restarting the queue from the
  // top, same as if this were the very first attract-mode session.
  const advance = () => setIndex((i) => (i + 1) % playlist.length);

  const handleTimeUpdate = (e) => {
    const { currentTime, duration } = e.currentTarget;
    if (duration > 0) setProgress((currentTime / duration) * 100);
  };

  if (!current) return null;

  const isDarkText = current.text_color === 'black';

  return (
    <div
      className={`kiosk-attract-loop${isDarkText ? ' kiosk-attract-loop--dark-text' : ''}`}
      role="presentation"
      onClick={onDismiss}
      onTouchStart={onDismiss}
    >
      <video
        ref={videoRef}
        key={current.video_url}
        className="kiosk-attract-loop__video"
        src={current.video_url}
        autoPlay
        muted
        playsInline
        onEnded={advance}
        onError={advance}
        onTimeUpdate={handleTimeUpdate}
      />
      <div className="kiosk-attract-loop__scanline" />

      {/* Broadcast-monitor corner frame — top corners left bare for the
       * brand wordmark (left) and mascot (right) below. */}
      <div className="kiosk-attract-loop__corner kiosk-attract-loop__corner--bl" />
      <div className="kiosk-attract-loop__corner kiosk-attract-loop__corner--br" />

      <div className="kiosk-attract-loop__brand">
        <span className="kiosk-attract-loop__brand-dot" />
        Bluppimart
      </div>

      <img className="kiosk-attract-loop__mascot" src="/bluppi-mascot.png" alt="" aria-hidden="true" />

      {(current.title || current.instagram_username) && (
        <div className="kiosk-attract-loop__info">
          {current.title && (
            <div className="kiosk-attract-loop__title">{current.title}</div>
          )}
          {current.instagram_username && (
            <div className="kiosk-attract-loop__instagram">
              <span>@{current.instagram_username}</span>
            </div>
          )}
        </div>
      )}

      <div className="kiosk-attract-loop__tap-hint" aria-label="Toca la pantalla para ver el catálogo">
        <img src="/tap-icon.png" alt="" aria-hidden="true" />
      </div>

      {playlist.length > 1 && (
        <button
          type="button"
          className="kiosk-attract-loop__picker-button"
          data-kiosk-keep-idle
          onTouchStart={stopTap}
          onClick={openPicker}
        >
          Ver videos
        </button>
      )}

      <div className="kiosk-attract-loop__progress-track">
        <div className="kiosk-attract-loop__progress-fill" style={{ width: `${progress}%` }} />
      </div>

      {pickerOpen && (
        <div
          className="kiosk-attract-loop__picker"
          role="dialog"
          aria-label="Elige un video"
          data-kiosk-keep-idle
          onTouchStart={stopTap}
          onClick={stopTap}
          onPointerDown={() => setPickerActivity((n) => n + 1)}
        >
          <div className="kiosk-attract-loop__picker-header">
            <h2 className="kiosk-attract-loop__picker-title">Elige un video</h2>
            <button
              type="button"
              className="kiosk-attract-loop__picker-close"
              onClick={() => setPickerOpen(false)}
            >
              Cerrar
            </button>
          </div>
          <div className="kiosk-attract-loop__picker-grid">
            {playlist.map((video, i) => (
              <button
                type="button"
                key={video.id ?? video.video_url}
                className={`kiosk-attract-loop__picker-item${i === index % playlist.length ? ' is-current' : ''}`}
                aria-current={i === index % playlist.length ? 'true' : undefined}
                onClick={() => choose(i)}
              >
                {/* First frame as the thumbnail (#t= media fragment) — no
                 * separate thumbnail upload needed, and only metadata loads. */}
                <video
                  className="kiosk-attract-loop__picker-thumb"
                  src={`${video.video_url}#t=0.5`}
                  preload="metadata"
                  muted
                  playsInline
                  aria-hidden="true"
                />
                <span className="kiosk-attract-loop__picker-label">
                  {video.title || (video.instagram_username ? `@${video.instagram_username}` : `Video ${i + 1}`)}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default KioskAttractLoop;
